import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  bindNativeDeviceContentOwner,
  clearNativeDeviceContent,
  discardAthenaContextItem,
  drainNativeSharedContent,
  normalizeNativeSharedContent,
  observeNativeSharedContent,
} from './AthenaCapabilities';

const PendingDeviceContext = createContext(null);
const MAX_PENDING_ITEMS = 8;

function collectAttachments(item) {
  if (!item) return [];
  if (Array.isArray(item.attachments)) return item.attachments;
  return item.localRef?.attachmentId ? [item] : [];
}

/**
 * Private device context is deliberately separate from the chat prompt. A
 * received share only becomes part of a draft after the person taps “usar”.
 */
export function PendingDeviceContextProvider({ children }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const seenIdsRef = useRef(new Set());

  const addIncoming = useCallback((raw) => {
    const item = normalizeNativeSharedContent(raw);
    if (!item || seenIdsRef.current.has(item.id)) return;
    seenIdsRef.current.add(item.id);
    setItems((previous) => {
      const next = [...previous, item];
      return next.length > MAX_PENDING_ITEMS ? next.slice(next.length - MAX_PENDING_ITEMS) : next;
    });
  }, []);

  useEffect(() => {
    if (!user?.id) {
      seenIdsRef.current = new Set();
      setItems([]);
      return undefined;
    }

    let active = true;
    let removeListener = () => {};
    seenIdsRef.current = new Set();
    setItems([]);

    const bootstrap = async () => {
      const owner = String(user.id);
      const binding = await bindNativeDeviceContentOwner(owner);
      if (!active) return;
      if (binding?.ownerChanged) {
        seenIdsRef.current = new Set();
        setItems([]);
      }

      removeListener = await observeNativeSharedContent((raw) => {
        if (active) addIncoming(raw);
      });

      const pending = await drainNativeSharedContent(owner);
      if (!active || pending?.ownerChanged) return;
      pending.items.forEach(addIncoming);
    };

    void bootstrap();
    return () => {
      active = false;
      void removeListener?.();
    };
  }, [addIncoming, user?.id]);

  const removeItem = useCallback(async (itemId, { discard = true } = {}) => {
    let removed = null;
    setItems((previous) => {
      removed = previous.find((item) => item.id === itemId) || null;
      return previous.filter((item) => item.id !== itemId);
    });
    if (!removed) return null;
    seenIdsRef.current.delete(removed.id);
    if (discard) await Promise.all(collectAttachments(removed).map(discardAthenaContextItem));
    return removed;
  }, []);

  const clear = useCallback(async () => {
    const current = items;
    setItems([]);
    seenIdsRef.current = new Set();
    await Promise.all(current.flatMap(collectAttachments).map(discardAthenaContextItem));
    await clearNativeDeviceContent();
  }, [items]);

  const value = useMemo(() => ({
    items,
    hasPending: items.length > 0,
    // `consume` removes a chip from the shared inbox but preserves its native
    // cache attachment so the composer can explicitly upload it later.
    consume: (itemId) => removeItem(itemId, { discard: false }),
    remove: (itemId) => removeItem(itemId, { discard: true }),
    clear,
  }), [clear, items, removeItem]);

  return <PendingDeviceContext.Provider value={value}>{children}</PendingDeviceContext.Provider>;
}

export function usePendingDeviceContext() {
  const context = useContext(PendingDeviceContext);
  if (!context) throw new Error('usePendingDeviceContext must be inside PendingDeviceContextProvider');
  return context;
}

